import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  Box,
  Card,
  Stack,
  Alert,
  Button,
  MenuItem,
  TextField,
  Container,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Divider
} from '@mui/material';

import { usersApi, type CreateUserRequest } from 'src/api/users.api';
import { companyApi } from 'src/api/company.api';
import { getAllBranchesWithPagination, getBranchById, type BranchDto } from 'src/api/branch_api_data';
import { useRouter } from 'src/routes/hooks';

// User roles for dropdown
const USER_ROLES = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'MANAGER', label: 'Yönetici' },
  { value: 'USER', label: 'Kullanıcı' }
] as const;

type UserRole = 'ADMIN' | 'MANAGER' | 'USER';

// New form data interface
interface NewUserFormData {
  name: string;
  username: string;
  role: UserRole;
  companyId: string;
  branchId: string;
  position: string;
  sendActivationEmail: boolean;
  password: string;
  confirmPassword: string;
  // ADMIN rolü için: işaretlenirse şube/pozisyon istenir ve Employee satırı oluşur,
  // böylece admin de mobil uygulamadan giriş/çıkış yapabilir.
  adminMobileAttendance: boolean;
}

// Company and Branch interfaces for dropdowns
interface CompanyOption {
  id: string;
  name: string;
}

interface BranchOption {
  id: string;
  name: string;
  companyId?: string;
}

export default function EmployeeCreateView() {
  const [searchParams] = useSearchParams();
  const router = useRouter();
  
  // States
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [branches, setBranches] = useState<BranchOption[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  
  // Form data state
  const [formData, setFormData] = useState<NewUserFormData>({
    name: '',
    username: '',
    role: 'USER',
    companyId: '',
    branchId: '',
    position: '',
    sendActivationEmail: true,
    password: '',
    confirmPassword: '',
    adminMobileAttendance: false
  });

  // Load initial data
  const loadInitialData = useCallback(async () => {
    try {
      setLoadingData(true);
      
      // Load companies
      const companiesData = await companyApi.getCompaniesForCurrentUser();
      setCompanies(companiesData);
      
      // Load all branches
      const branchesData = await getAllBranchesWithPagination(0, 100, 'name', 'asc');
      setBranches(branchesData.content || []);
      
      // Pre-populate if branchId is provided in URL
      const branchId = searchParams.get('branchId');
      if (branchId) {
        try {
          const branchData = await getBranchById(branchId);
          setFormData(prev => ({
            ...prev,
            branchId,
            companyId: branchData.companyId || ''
          }));
        } catch (branchError) {
          console.warn('Could not load branch data for pre-population:', branchError);
        }
      }
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Form verileri yüklenirken hata oluştu');
    } finally {
      setLoadingData(false);
    }
  }, [searchParams]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Handle form field changes
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle company change (triggers branch filter)
  const handleCompanyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const companyId = event.target.value;
    setFormData(prev => ({
      ...prev,
      companyId,
      branchId: '' // Reset branch selection
    }));
  };

  // Form validation
  const validateForm = (): string | null => {
    if (!formData.name.trim()) return 'Ad Soyad alanı zorunludur';
    if (!formData.username.trim()) return 'E-posta alanı zorunludur';
    if (!formData.username.includes('@')) return 'Geçerli bir e-posta adresi giriniz';
    if (!formData.companyId) return 'Şirket seçimi zorunludur';
    
    const requiresBranch = formData.role !== 'ADMIN'
      || (formData.role === 'ADMIN' && formData.adminMobileAttendance);
    if (requiresBranch) {
      if (!formData.branchId) return 'Şube seçimi zorunludur';
      if (!formData.position.trim()) return 'Pozisyon alanı zorunludur';
    }
    
    if (!formData.sendActivationEmail) {
      if (!formData.password) return 'Şifre alanı zorunludur';
      if (formData.password.length < 6) return 'Şifre en az 6 karakter olmalıdır';
      if (formData.password !== formData.confirmPassword) return 'Şifreler eşleşmiyor';
    }
    
    return null;
  };

  // Submit form
  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Prepare payload for backend with complete data
      const createUserPayload: CreateUserRequest = {
        name: formData.name,
        username: formData.username,
        password: formData.sendActivationEmail ? null : formData.password,
        authorities: [`ROLE_${formData.role}`],
        companyId: formData.companyId,
        branchId: (formData.role !== 'ADMIN' || formData.adminMobileAttendance)
          ? formData.branchId
          : undefined,
        position: (formData.role !== 'ADMIN' || formData.adminMobileAttendance)
          ? formData.position
          : undefined,
        sendActivationEmail: formData.sendActivationEmail
      };

      console.log('Sending user creation payload:', createUserPayload);
      await usersApi.createUser(createUserPayload);
      router.back();
    } catch (err: any) {
      console.error('User creation error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Kullanıcı oluşturulurken bir hata oluştu';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Dynamic field visibility
  const shouldShowCompany = true;
  const shouldShowAdminMobileToggle = formData.role === 'ADMIN';
  const shouldShowBranch = formData.role !== 'ADMIN' || formData.adminMobileAttendance;
  const shouldShowPosition = formData.role !== 'ADMIN' || formData.adminMobileAttendance;
  const shouldShowPasswordFields = !formData.sendActivationEmail;

  // Filtered branches based on selected company
  const filteredBranches = branches.filter(branch => 
    !formData.companyId || branch.companyId === formData.companyId
  );

  if (loadingData) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Form yükleniyor...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container>
        <Box mb={5}>
          <Typography variant="h4">Yeni Kullanıcı Ekle</Typography>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        <Card>
          <Stack spacing={3} sx={{ p: 3 }}>
            {/* Ad Soyad - Birleştirilmiş */}
            <TextField
              label="Ad Soyad"
              name="name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              required
              helperText="Örnek: Ahmet Yılmaz"
            />

            {/* E-posta (Username) */}
            <TextField
              label="E-posta"
              name="username"
              type="email"
              value={formData.username}
              onChange={handleChange}
              fullWidth
              required
              helperText="Bu e-posta kullanıcı adı olarak kullanılacak"
            />

            {/* Rol Dropdown */}
            <TextField
              select
              label="Rol"
              name="role"
              value={formData.role}
              onChange={handleChange}
              fullWidth
              required
              helperText="Kullanıcının sistem rolü"
            >
              {USER_ROLES.map((role) => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </TextField>

            {/* ADMIN için mobil mesai takibi seçeneği */}
            {shouldShowAdminMobileToggle && (
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="adminMobileAttendance"
                      checked={formData.adminMobileAttendance}
                      onChange={handleChange}
                    />
                  }
                  label="Mobil mesai takibini etkinleştir"
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>
                  İşaretlenirse bu yönetim hesabı, atanan şubede mobil uygulamadan giriş/çıkış yapabilir.
                </Typography>
              </Box>
            )}

            {/* Şirket Dropdown - Her zaman görünür */}
            {shouldShowCompany && (
              <TextField
                select
                label="Şirket"
                name="companyId"
                value={formData.companyId}
                onChange={handleCompanyChange}
                fullWidth
                required
                helperText="Kullanıcının bağlı olduğu şirket"
              >
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {/* Şube Dropdown - Sadece MANAGER ve USER için */}
            {shouldShowBranch && (
              <TextField
                select
                label="Şube"
                name="branchId"
                value={formData.branchId}
                onChange={handleChange}
                fullWidth
                required
                disabled={!formData.companyId}
                helperText={
                  !formData.companyId 
                    ? "Önce şirket seçiniz" 
                    : `${filteredBranches.length} şube mevcut`
                }
              >
                {filteredBranches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.name}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {/* Pozisyon - Sadece MANAGER ve USER için */}
            {shouldShowPosition && (
              <TextField
                label="Pozisyon"
                name="position"
                value={formData.position}
                onChange={handleChange}
                fullWidth
                required
                helperText="Kullanıcının iş pozisyonu"
                placeholder="Örnek: Operasyon Uzmanı, Satış Temsilcisi"
              />
            )}

            <Divider />

            {/* Aktivasyon Maili Checkbox */}
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    name="sendActivationEmail"
                    checked={formData.sendActivationEmail}
                    onChange={handleChange}
                  />
                }
                label="Aktivasyon maili gönder"
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>
                İşaretlenirse kullanıcıya aktivasyon maili gönderilir ve şifre belirleyebilir
              </Typography>
            </Box>

            {/* Şifre Alanları - Sadece aktivasyon maili seçili değilse */}
            {shouldShowPasswordFields && (
              <>
                <TextField
                  label="Şifre"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  fullWidth
                  required
                  helperText="En az 6 karakter olmalıdır"
                />
                <TextField
                  label="Şifre Tekrar"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  fullWidth
                  required
                  helperText="Şifreyi tekrar giriniz"
                  error={formData.confirmPassword !== '' && formData.password !== formData.confirmPassword}
                />
              </>
            )}

            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
              <Button 
                onClick={() => router.back()}
                disabled={loading}
              >
                İptal
              </Button>
              <Button 
                variant="contained" 
                onClick={handleSubmit} 
                color="primary"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Kaydediliyor...' : 'Kaydet'}
              </Button>
            </Stack>
          </Stack>
        </Card>
      </Container>
  );
}
